// File: src/components/sections/FeatureSection/FeatureSection.tsx
import React from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { DefaultSection } from "./DefaultSection";
import { Typography } from "../../general/Typography";
import { LucideIcon } from "lucide-react";

interface Feature {
  icon: LucideIcon;
  title: string;
  description: string;
}

interface FeatureSectionProps {
  sectionTitle?: string;
  sectionDescription?: string;
  features: Feature[];
}

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: ${({ theme }) => theme.spacing.xl};
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: flex;
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    padding-bottom: ${({ theme }) => theme.spacing.lg};

    & > * {
      scroll-snap-align: center;
      flex: 0 0 80%;
      max-width: 80%;
    }
  }
`;

const FeatureCard = styled(motion.div)`
  background: ${({ theme }) => theme.colors.surface || "rgba(255,255,255,0.75)"};
  border-radius: ${({ theme }) => theme.radii.lg};
  padding: ${({ theme }) => theme.spacing.xl};
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: ${({ theme }) => theme.spacing.md};
  box-shadow: ${({ theme }) => theme.elevations.md};
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;

  &:hover {
    transform: scale(1.03);
    box-shadow: ${({ theme }) => theme.elevations.high};
  }
`;

const IconWrapper = styled.div`
  background: ${({ theme }) => theme.gradients.radialPrimary};
  padding: ${({ theme }) => theme.spacing.sm};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const FeatureSection: React.FC<FeatureSectionProps> = ({
  sectionTitle = "What Makes ELI Unique?",
  sectionDescription = "Social, gamified, AI-driven learning for creators and students.",
  features,
}) => {
  return (
    <DefaultSection
      sectionId="features"
      title={sectionTitle}
      subtitle={sectionDescription}
      fullHeight={false}
      backgroundColor="#f9f9f9"
      animateOnScroll
      snap={false}
      content={
        <FeatureGrid>
          {features.map(({ icon: Icon, title, description }, i) => (
            <FeatureCard
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: i * 0.1 }}
              whileHover={{ scale: 1.05 }}
            >
              <IconWrapper>
                <Icon size={28} color="#fff" />
              </IconWrapper>
              <Typography as="h3" variant="heading3">
                {title}
              </Typography>
              <Typography variant="body">{description}</Typography>
            </FeatureCard>
          ))}
        </FeatureGrid>
      }
    />
  );
};